import React, { useMemo } from 'react';
import { SharedValue } from 'react-native-reanimated';

import { EventInfo, isTeamEvent } from '@/api/events/types/types';
import { getPrematchHudHeight } from '@/components/event-hud/utils';
import { EventSheetTitle } from '@/components/event-sheet/EventSheetTitle';
import { getFinalScoreColumns } from '@/feature/bets-sbk/utils/utils';
import { leagueConfigSelector, useLeagueConfigsStore } from '@/feature/betslip-pickem/hooks/use-league-configs-store';
import { PICKS_USE_EVENT_STALE_TIME, useEvent } from '@/feature/event-details-sbk/hooks/use-event';
import { getHudHeight } from '@/feature/event-details-sbk/utils/get-hud-height';
import { useEventSupportsScoreboard } from '@/hooks/use-event-supports-scoreboard';
import { useFormatPeriod } from '@/hooks/use-format-period';
import { useGameTrackerConfig } from '@/hooks/use-game-tracker-config';
import { useEventDetailsCache } from '@/hooks/use-sbk-event-details-cache';
import { EventStatus } from '@/types/api.generated';
import { gameUtils } from '@/utils/games';
import { useShallow } from 'zustand/react/shallow';

import { convertPickemEventToHudEvent } from './PickemEventHud';

export const FantasyEventSheetTitle = ({
    event,
    scrollOffset,
    isOnlyDedicatedHeader,
}: {
    event: EventInfo;
    isOnlyDedicatedHeader?: boolean;
    scrollOffset: SharedValue<number>;
}) => {
    const { leagueLabel } = useLeagueConfigsStore(useShallow(leagueConfigSelector(event.league)));
    const eventTitle = isOnlyDedicatedHeader ? leagueLabel || event.league : gameUtils.createTitleLabel(event);
    const eventSubtitle = gameUtils.createEventSubtitle(event);

    const hasTeams = isTeamEvent(event);

    const pamEventId = event?.dataFeedSourceIds.find(({ source }) => source === 'PAM')?.id ?? '';
    const fetchPamEvent = pamEventId !== '' && gameUtils.isLive(event);

    const { data: pamEvent } = useEvent(pamEventId, { enabled: fetchPamEvent, staleTime: PICKS_USE_EVENT_STALE_TIME });

    const showScoreboard = !!useEventSupportsScoreboard(event);
    const gameTrackerConfig = useGameTrackerConfig(pamEvent?.league, pamEvent?.event_details);

    // if the PAM event is available, use it for ordering the players to avoid a mismatch between player and score
    const eventHud = convertPickemEventToHudEvent(event, pamEvent);

    const pamEventDetails = useEventDetailsCache(state => state.eventDetails[pamEventId] ?? pamEvent?.event_details);

    const finalScores = useMemo(
        () => (pamEvent && pamEventDetails ? getFinalScoreColumns(pamEventDetails, pamEvent?.sport)[0] : undefined),
        [pamEventDetails, pamEvent]
    );

    const livePeriodLabel = useFormatPeriod({
        event: pamEvent,
        periodName: pamEventDetails?.period_name,
        gameClock: pamEventDetails?.game_clock,
        sportSpecific: pamEventDetails?.sport_specific,
        customSeparator: '\n',
    });

    const hudHeight = pamEvent
        ? getHudHeight({
              eventStatus: pamEvent?.status,
              sportName: pamEvent?.sport?.name,
              showScoreboard,
          })
        : getPrematchHudHeight(eventHud);

    const eventDetails = useMemo(
        () => ({
            matchDetails: livePeriodLabel,
            league: event.league,
            eventTime: gameUtils.createTimeLabel(event),
            isLive: event.status === EventStatus.InProgress,
        }),
        [livePeriodLabel, event]
    );

    const teamDetails = useMemo(
        () => ({
            awayTeam: eventHud?.awayTeam,
            homeTeam: eventHud?.homeTeam,
            awayScore: finalScores?.awayScore,
            homeScore: finalScores?.homeScore,
        }),
        [eventHud, finalScores]
    );

    return (
        <EventSheetTitle
            hasTeams={hasTeams}
            eventTitle={eventTitle}
            showGameTracker={!!gameTrackerConfig}
            eventSubTitle={eventSubtitle}
            pamEvent={pamEvent}
            scrollOffset={scrollOffset}
            hudHeight={hudHeight}
            teamDetails={teamDetails}
            eventDetails={eventDetails}
        />
    );
};
